// Web Push on this device — architecture §3.7, D7. One place for: is push possible here,
// is it on, turning it on/off, keeping the server's Device row in step with the browser.
import { useCallback, useEffect, useState } from 'react';
import { platform } from '../platform/web.js';
import { api } from './api.js';

export type PushState =
  | 'checking'
  | 'unsupported' // browser has no Web Push (or no service worker, e.g. `pnpm dev`)
  | 'needs-install' // iOS Safari: push works only from the Home Screen app (architecture §3.7)
  | 'denied' // blocked in browser settings; only the user can undo it
  | 'off'
  | 'on';

let cachedKey: Promise<string | null> | null = null;

/** VAPID public key from the API (always matches the Worker's signing key). */
function vapidKey(): Promise<string | null> {
  cachedKey ??= api
    .pushConfig()
    .then((r) => r.vapid_public_key ?? import.meta.env.VITE_VAPID_PUBLIC_KEY ?? null)
    .catch(() => {
      cachedKey = null;
      return import.meta.env.VITE_VAPID_PUBLIC_KEY ?? null;
    });
  return cachedKey;
}

function supported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function readPushState(): Promise<PushState> {
  if (platform.isIosSafari() && !platform.isStandalone()) return 'needs-install';
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'off';
  return (await platform.currentPushEndpoint()) ? 'on' : 'off';
}

/** Asks for permission (call from a tap), subscribes, and registers this device. */
export async function enablePush(): Promise<PushState> {
  const key = await vapidKey();
  if (!key) throw new Error('Push is not configured on the server');
  const subscription = await platform.registerPush(key);
  if (!subscription) return readPushState();
  await api.registerDevice({ platform: 'web', push_subscription: subscription });
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const endpoint = await platform.unregisterPush();
  if (endpoint) await api.unregisterDevice(endpoint).catch(() => undefined);
  return readPushState();
}

/**
 * After sign-in: if this browser already allows push, (re)register it for the signed-in
 * account. Covers a new account on a shared device, a rotated key, and an expired row.
 * Never prompts.
 */
export async function syncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return;
  const key = await vapidKey();
  if (!key) return;
  const subscription = await platform.registerPush(key);
  if (subscription) await api.registerDevice({ platform: 'web', push_subscription: subscription });
}

/** Before sign-out: stop this device receiving the account's pushes. Keeps browser permission. */
export async function detachPush(): Promise<void> {
  if (!supported()) return;
  const endpoint = await platform.currentPushEndpoint();
  if (endpoint) await api.unregisterDevice(endpoint);
}

export function usePush() {
  const [state, setState] = useState<PushState>('checking');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void readPushState().then((s) => live && setState(s));
    return () => {
      live = false;
    };
  }, []);

  const run = useCallback(async (action: () => Promise<PushState>) => {
    setBusy(true);
    try {
      const next = await action();
      setState(next);
      return next;
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    state,
    busy,
    enable: () => run(enablePush),
    disable: () => run(disablePush),
  };
}
